// El sheet de CALIDAD de una generación de imágenes, para las cards del chat —
// ADR-2026-09-13-la-calidad-de-imagen-se-elige-en-la-card.
//
// Es el gemelo de `FormatPicker` para la resolución: misma escalera de sheet
// (`SheetLateral`, portaleado), misma forma de usarse (elegir cierra), y las opciones
// salen de `OPCIONES_RESOLUCION` para que la card diga lo mismo que los modos.
//
// Muestra el COSTO TOTAL de cada opción, no la tarifa por imagen: en un lote de 20 la
// diferencia entre "2 créditos" y "40 créditos" es lo único que el usuario necesita
// mirar para decidir, y hacer esa multiplicación de cabeza es justo lo que hace que
// alguien toque 4K sin darse cuenta.

import React from 'react';
import { Check } from 'lucide-react';
import { SheetLateral } from '../../../components/SheetLateral';
import {
  creditosPorResolucion,
  OPCIONES_RESOLUCION,
  type ResolucionImagen,
} from '../../../lib/resolucionImagen';

export interface CalidadImagenSheetProps {
  open: boolean;
  /** La resolución elegida hoy (ya resuelta: preferencia o default). */
  seleccionada: ResolucionImagen;
  onChange: (id: ResolucionImagen) => void;
  onClose: () => void;
  /** Cuántas imágenes entrega esta generación, para mostrar el costo total. */
  cantidadDeImagenes: number;
}

export const CalidadImagenSheet: React.FC<CalidadImagenSheetProps> = ({
  open,
  seleccionada,
  onChange,
  onClose,
  cantidadDeImagenes,
}) => {
  const cantidad = Math.max(1, cantidadDeImagenes);
  return (
    <SheetLateral open={open} onClose={onClose} titulo="Calidad" altoPorContenido>
      <div className="flex flex-col gap-2">
        {OPCIONES_RESOLUCION.map((opt) => {
          const elegida = opt.id === seleccionada;
          const costo = creditosPorResolucion(opt.id) * cantidad;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                onChange(opt.id);
                onClose();
              }}
              className={`flex items-center gap-3 rounded-2xl border-2 bg-white py-3 px-4 text-left transition-colors active:scale-[0.99] ${
                elegida ? 'border-brand-primary' : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-[#02062B]/[0.9]">{opt.label}</p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-[#02062B]/[0.55]">
                  {opt.detalle}
                </p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#F7F7F9] px-2.5 py-1 text-[12.5px] font-semibold text-brand-black">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="h-3.5 w-3.5 object-contain"
                />
                {costo}
              </span>
              {elegida && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[12px] leading-snug text-[#02062B]/[0.5]">
        {cantidad === 1
          ? 'El costo es por esta imagen.'
          : `El costo es por las ${cantidad} imágenes de esta generación.`}{' '}
        Podés dejar una calidad fija para todo en el menú “+” → Preferencias de contenido.
      </p>
    </SheetLateral>
  );
};

export default CalidadImagenSheet;
